import React from 'react';
import pt from 'prop-types';
import Status from 'components/status';
import Tooltip from 'components/tooltip';
import { formatDate } from 'utils';
import { publishing } from 'components/storefront/editor/utils/storefront';

const DEFAULT_DATE_FORMAT = 'shortExact';

export class HeaderStatus extends React.PureComponent {
  static propTypes = {
    children: pt.any,
  };

  render() {
    const { children, ...props } = this.props;

    return (
      <Status
        className="storefront-editor--status"
        size="sm"
        tag={false}
        dot
        {...props}
      >
        {children}
      </Status>
    );
  }
}

export class HeaderStatusTooltip extends React.PureComponent {
  static propTypes = {
    message: pt.node,
    children: pt.any,
  };

  render() {
    const { message, children } = this.props;

    return (
      <Tooltip message={message} side="bottom" arrow={false}>
        {children}
      </Tooltip>
    );
  }
}

export default class StorefrontEditorHeaderStatus extends React.PureComponent {
  static propTypes = {
    storefront: pt.object,
    saving: pt.bool,
  };

  getStorefront() {
    return this.props.storefront;
  }

  getTheme() {
    const storefront = this.getStorefront();

    return storefront.theme;
  }

  getUpdateDate() {
    const theme = this.getTheme();

    return theme.date_updated;
  }

  getPublishDate() {
    const storefront = this.getStorefront();

    return storefront.date_published;
  }

  getUpdaterName() {
    const theme = this.getTheme();

    return theme.updated_user?.name;
  }

  getPublisherName() {
    const theme = this.getTheme();

    return theme.published_user?.name;
  }

  getChangeCount() {
    const theme = this.getTheme();

    return theme.config_version - theme.published_config_version;
  }

  hasChanges() {
    return this.getChangeCount() > 0;
  }

  formatDate(date, format = DEFAULT_DATE_FORMAT) {
    return formatDate(date, format);
  }

  getFormattedUpdateDate(format) {
    const updateDate = this.getUpdateDate();

    return this.formatDate(updateDate, format);
  }

  getFormattedPublishDate(format) {
    const publishDate = this.getPublishDate();

    return this.formatDate(publishDate, format);
  }

  renderSaving() {
    return (
      <HeaderStatus type={null} loading>
        Saving
      </HeaderStatus>
    );
  }

  renderPublishing() {
    return (
      <HeaderStatus type={null} loading>
        Publishing
      </HeaderStatus>
    );
  }

  getSavedMessage() {
    const updaterName = this.getUpdaterName();

    return (
      <span>
        Last changed&nbsp;
        {updaterName && (
          <>
            by <span className="bold">{updaterName}</span>
            <br />
          </>
        )}
        {this.getFormattedUpdateDate()}
      </span>
    );
  }

  renderSaved() {
    return (
      <HeaderStatusTooltip message={this.getSavedMessage()}>
        <HeaderStatus type={null} dot={false}>
          Saved
        </HeaderStatus>
      </HeaderStatusTooltip>
    );
  }

  renderUnpublished() {
    return <HeaderStatus type="muted">Not yet published</HeaderStatus>;
  }

  getPublishedWithChangesMessage() {
    const changeCount = this.getChangeCount();
    const publisherName = this.getPublisherName();

    return (
      <span>
        <span className="bold">{changeCount} changes</span> since last published
        {publisherName ? (
          <>
            &nbsp;by
            <br />
            <span className="bold">{publisherName}&nbsp;</span>
          </>
        ) : (
          <br />
        )}
        {this.getFormattedPublishDate()}
      </span>
    );
  }

  renderPublishedWithChanges() {
    return (
      <HeaderStatusTooltip message={this.getPublishedWithChangesMessage()}>
        <HeaderStatus type="warning">Published</HeaderStatus>
      </HeaderStatusTooltip>
    );
  }

  getPublishedMessage() {
    const publisherName = this.getPublisherName();

    return (
      <span>
        Last published&nbsp;
        {publisherName && (
          <>
            by <span className="bold">{publisherName}</span>
            <br />
          </>
        )}
        {this.getFormattedPublishDate()}
      </span>
    );
  }

  renderPublished() {
    return (
      <HeaderStatusTooltip message={this.getPublishedMessage()}>
        <HeaderStatus type="positive">Published</HeaderStatus>
      </HeaderStatusTooltip>
    );
  }

  render() {
    const { storefront, saving } = this.props;
    const { primary, date_published: datePublished } = storefront;

    if (!this.getTheme()) {
      return null;
    }

    if (saving) {
      return this.renderSaving();
    }

    if (!primary) {
      return this.renderSaved();
    }

    if (publishing(storefront)) {
      return this.renderPublishing();
    }

    if (!datePublished) {
      return this.renderUnpublished();
    }

    if (this.hasChanges()) {
      return this.renderPublishedWithChanges();
    }

    return this.renderPublished();
  }
}
